import { DebugControlsService, PageInfoService, RouteHelperService } from '@pulse/browser-shared';
import { ExperimentInfo, PulseStorage, SettingsConfigurations } from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';

export class ExperimentDebugService {
    private experiment: ExperimentInfo | null = null;
    private experimentSubject$ = new ReplaySubject<ExperimentInfo | null>(1);

    constructor(
        private storage: PulseStorage,
        protected readonly cookieStorage: PulseStorage,
        private page: PageInfoService,
        private config: SettingsConfigurations,
        private routeHelper: RouteHelperService,
        private debugControlService: DebugControlsService,
    ) {
        const url = new URL(this.page.landingPageUrl());
        const str = url.searchParams.get('pulse-debug-exp') || this.storage.get('debug-exp');
        const experiment = this.parseExperimentInfo(str);
        //TODO: remove after 2 moths of the release
        this.cookieStorage.remove('debug-exp');

        this.routeHelper.cleanupURL('pulse-debug-exp');

        if (!str || !experiment) {
            // Emit null if no experiment is found
            this.experimentSubject$.next(null);

            return;
        }

        if (str) {
            this.storage.set('debug-exp', str);
        }

        this.setExperiment(experiment);

        this.debugControlService.registerControl({
            control: 'experiment',
            url: experiment.url,
            title: `Debugging Experiment [${experiment.id}]: ${experiment.name}\n\nVariant: ${experiment.variant}`,
            callbackFn: this.stopDebugging.bind(this),
            content: `${experiment.variant} ${experiment.name}`,
        });
    }

    getDebugExperiment(): ExperimentInfo | null {
        return this.experiment;
    }

    /**
     * Get an observable that emits debug experiment changes.
     * Emits the current debug experiment when subscribed and whenever it changes.
     */
    getDebugExperiment$(): Observable<ExperimentInfo | null> {
        return this.experimentSubject$.asObservable();
    }

    private setExperiment(experiment: ExperimentInfo | null): void {
        this.experiment = experiment;
        // Emit the new experiment to subscribers
        this.experimentSubject$.next(experiment);
    }

    private stopDebugging(): void {
        this.setExperiment(null);
        this.storage.remove('debug-exp');
        this.debugControlService.unregisterControl('experiment');
    }

    private parseExperimentInfo(experimentInfo: string | null | undefined): ExperimentInfo | null {
        if (typeof experimentInfo !== 'string' || !experimentInfo.length) {
            return null;
        }

        try {
            const [app, id, name, variant, url] = JSON.parse(experimentInfo);

            if (app !== this.config.app) {
                return null;
            }

            return { app, id, name, variant, url };
        } catch (ex) {
            console.error('Failed to parse debug experiment info!');
            console.error(ex);

            return null;
        }
    }
}
